import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import type { EquipmentChange, EquipmentOptionView } from "@slfinrtl/ui-bff-routes"
import { useCallback, useState } from "react"
import type { EditableEquipmentListProps } from "src/components"
import type { AddCustomEquipmentModalProps, RichCustomEquipment } from "src/containers/addCustomEquipment"
import { AddCustomEquipmentModal } from "src/containers/addCustomEquipment"
import { useModal } from "src/context/modal/useModal"

export interface UseSpecialEquipmentListEditReturn {
	getChange: () => Pick<
		EquipmentChange,
		"addCustomEquipment" | "removeCustomEquipmentByNames" | "removeCatalogEquipmentByOptionCodes"
	>
	resetChanges: () => void
	view: EditableEquipmentListProps
}
export const useSpecialEquipmentListEdit = ({
	view,
}: {
	view: EquipmentOptionView[]
}): UseSpecialEquipmentListEditReturn => {
	const [addedCustomEquipment, setAddedCustomEquipment] = useState<RichCustomEquipment[]>([])
	const [removedCustomEquipmentByNames, setRemovedCustomEquipmentByNames] = useState<string[]>([])
	const [removedCatalogEquipmentByOptionCodes, setRemovedCatalogEquipmentByOptionCodes] = useState<string[]>([])

	const { availableLocales, locale } = useDealerContext()

	const onAddCustomEquipment = useCallback((customEquipment: RichCustomEquipment) => {
		setAddedCustomEquipment((existing) => [...existing, customEquipment])
	}, [])
	const showAddCustomEquipmentModal = useModal<AddCustomEquipmentModalProps>(AddCustomEquipmentModal)
	const handleShowAddCustomEquipmentModalClicked = useCallback(
		() => showAddCustomEquipmentModal({ onAddCustomEquipment }),
		[onAddCustomEquipment, showAddCustomEquipmentModal],
	)

	const onRemoveEquipment = useCallback(
		(removedEquipment: EquipmentOptionView) => {
			if (removedEquipment.optionCode) {
				setRemovedCatalogEquipmentByOptionCodes([
					...removedCatalogEquipmentByOptionCodes,
					removedEquipment.optionCode,
				])
			} else {
				const addedEquipment = addedCustomEquipment.find((eq) =>
					Object.values(eq.name).includes(removedEquipment.name),
				)
				if (addedEquipment) {
					setAddedCustomEquipment(addedCustomEquipment.filter((eq) => eq !== addedEquipment))
				} else {
					setRemovedCustomEquipmentByNames([...removedCustomEquipmentByNames, removedEquipment.name])
				}
			}
		},
		[addedCustomEquipment, removedCatalogEquipmentByOptionCodes, removedCustomEquipmentByNames],
	)

	const getChange = useCallback(() => {
		return {
			addCustomEquipment: addedCustomEquipment.map((richCustomEquipment) => ({
				...richCustomEquipment,
				category: richCustomEquipment.category.key,
			})),
			removeCatalogEquipmentByOptionCodes: removedCatalogEquipmentByOptionCodes,
			removeCustomEquipmentByNames: removedCustomEquipmentByNames,
		}
	}, [addedCustomEquipment, removedCatalogEquipmentByOptionCodes, removedCustomEquipmentByNames])

	const resetChanges = useCallback(() => {
		setAddedCustomEquipment([])
		setRemovedCatalogEquipmentByOptionCodes([])
		setRemovedCustomEquipmentByNames([])
	}, [])

	return {
		getChange,
		resetChanges,
		view: {
			equipmentList: [
				...view.filter((eq) =>
					eq.optionCode
						? !removedCatalogEquipmentByOptionCodes.includes(eq.optionCode)
						: !removedCustomEquipmentByNames.includes(eq.name),
				),
				...addedCustomEquipment.map((customEquipment) => ({
					category: customEquipment.category.name,
					name: customEquipment.name[locale] ?? customEquipment.name[availableLocales[0]],
					optionCode: null,
					visibleForCustomers: true,
				})),
			],
			onAddCustomEquipment: handleShowAddCustomEquipmentModalClicked,
			onRemove: onRemoveEquipment,
		},
	}
}
